// Private storage (crew-docs is a PRIVATE bucket): every file link / image in the app is opened through a short-lived signed link,
// after the server checks the login may see a record holding that file (View on that module - can_read_file, same rules as the data).
// A record stores the file's PATH in the bucket (new uploads) or an old full public link; both are accepted everywhere
// (storedPath in src/lib/filePaths.js takes the path out of an old link).
import { useEffect, useState, createElement } from 'react'
import { supabase } from './supabase'
import { storedPath, fileLink, filePath } from './filePaths'
import { uploadProblem } from './uploadTypes'

const BASE = String(process.env.REACT_APP_SUPABASE_URL || '').replace(/\/$/, '')
const cache = new Map()   // path -> { signed, error, until }   error: 'denied' | 'not_found' | 'error' from the server
// a value the app may sign: a stored path, or a crew-docs link that is not signed yet (public / authenticated form)
const isFile = u => typeof u === 'string' && !/\/storage\/v1\/object\/sign\//.test(u) && storedPath(u) != null
const fresh = p => { const c = cache.get(p); return !!(c && c.until > Date.now()) }

async function authHeader() {
  const { data } = await supabase.auth.getSession()
  return 'Bearer ' + ((data && data.session && data.session.access_token) || '')
}

// stored values (paths or old links) -> { value: signed link | null }
export async function signUrls(values) {
  const list = (values || []).filter(isFile)
  const want = [...new Set(list.map(storedPath))].filter(p => !fresh(p))
  for (let i = 0; i < want.length; i += 50) {
    const part = want.slice(i, i + 50)
    try {
      const r = await fetch('/api/upload-cert', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: await authHeader() }, body: JSON.stringify({ action: 'sign', urls: part }) })
      const j = await r.json().catch(() => ({}))
      for (const p of part) cache.set(p, { signed: (j.links || {})[p] || null, error: (j.errors || {})[p] || (r.ok ? null : 'error'), until: Date.now() + 8 * 60 * 1000 })
    } catch (e) { /* leave uncached; caller falls back */ }
  }
  return Object.fromEntries((values || []).map(u => [u, isFile(u) ? (cache.get(storedPath(u)) || {}).signed || null : null]))
}
export async function signUrl(u) { return (await signUrls([u]))[u] }

// a manual's file (crew-docs/manuals/<vessel>/...): the login's own 10-minute signed link straight from storage (the storage rule
// lets Manuals View read its vessel's manuals folder; sql/manuals-2.sql refuses a manual it may not see). null = not a manuals
// file, or storage said no -> the caller asks the server (signUrl).
export const MANUAL_SIGN_SECONDS = 600
const MANUAL_PATH = /\/storage\/v1\/object\/(?:public|sign|authenticated)\/crew-docs\/(manuals\/[^?#]+)/
export const manualPathOf = u => { const s = String(u || ''); if (/^manuals\//.test(s)) return s; const m = s.match(MANUAL_PATH); if (!m) return null; try { return decodeURIComponent(m[1]) } catch (e) { return null } }
export async function signManualDirect(u) {
  const path = manualPathOf(u)
  if (!path) return null
  try {
    const { data, error } = await supabase.storage.from('crew-docs').createSignedUrl(path, MANUAL_SIGN_SECONDS)
    return !error && data && data.signedUrl ? data.signedUrl : null
  } catch (e) { return null }
}

// the text shown when a file cannot be opened
export function fileError(u) {
  const why = (cache.get(storedPath(u)) || {}).error
  return why === 'not_found' ? 'This file is no longer in storage - save or upload it again.' : why === 'error' ? 'The file could not be opened right now - try again.' : 'You do not have access to this file.'
}

async function openSigned(u0, win) {
  // "...pdf#page=12" (a cited manual page): sign the file, keep the page
  const [u, hash] = String(u0).split('#')
  const s = (await signManualDirect(u)) || await signUrl(u)
  if (s) win.location.href = s + (hash ? '#' + hash : '')
  else {
    try { win.close() } catch (e) {}
    alert(fileError(u))
  }
}

// open a stored file (path or old link) in a new tab, through a signed link
export function openFile(v) {
  if (!isFile(String(v || '').split('#')[0])) return null
  const w = (window.__hcOpen || window.open)('about:blank', '_blank')
  if (w) openSigned(v, w)
  return w
}

// href for an <a> showing a stored file: the full (public-form) link. The click handler below swaps it for a signed one,
// so the file never opens without the check. A value that is no stored file is returned as it is.
export const fileHref = v => fileLink(v, BASE)

// the file's bytes (canvas crop, image into a print / PDF): fetched through a signed link
export async function fetchFile(v, opts) {
  const s = await signUrl(v)
  if (!s) throw new Error(fileError(v))
  const r = await fetch(s, opts)
  if (!r.ok) throw new Error(r.status === 400 || r.status === 404 ? 'This file is no longer in storage - save or upload it again.' : 'The file could not be opened right now - try again.')
  return r
}

// React: the signed link of a stored file (null while it is being signed / when the login may not see it)
export function useSignedUrl(v) {
  const [s, setS] = useState(() => (isFile(v) && fresh(storedPath(v)) ? cache.get(storedPath(v)).signed : null))
  useEffect(() => {
    let live = true
    if (!isFile(v)) { setS(null); return undefined }
    signUrl(v).then(x => { if (live) setS(x) })
    return () => { live = false }
  }, [v])
  return s
}
// <FileImg src={stored value} />: an image loaded only through its signed link (no request to the private bucket first)
export function FileImg({ src, alt = '', ...rest }) {
  const s = useSignedUrl(src)
  if (!isFile(src)) return createElement('img', { src, alt, ...rest })
  return s ? createElement('img', { src: s, alt, ...rest }) : createElement('span', { 'aria-busy': true, className: rest.className, style: { display: 'inline-block', minWidth: 24, minHeight: 24, ...(rest.style || {}) } })
}

// upload into crew-docs/<area>/<vessel id>/[<item id>/]<timestamp>_<name> and return the PATH the record stores
export async function uploadStored(area, vesselId, file, { name, sub, contentType } = {}) {
  const bad = uploadProblem({ name: name || (file && file.name), type: contentType || (file && file.type), size: file && file.size })   // the bucket refuses the same (sql/sec-fix.sql)
  if (bad) throw new Error(bad)
  const path = filePath(area, vesselId, name || (file && file.name) || 'file', sub ? { sub } : {})
  const { error } = await supabase.storage.from('crew-docs').upload(path, file, { contentType: contentType || (file && file.type) || 'application/octet-stream', upsert: false })
  if (error) throw error
  return path
}

let installed = false
export function installFileAccess() {
  if (installed || typeof window === 'undefined') return
  installed = true
  // 1) links: open through a signed link
  document.addEventListener('click', e => {
    const a = e.target && e.target.closest && e.target.closest('a[href]')
    if (!a || !isFile(a.href.split('#')[0])) return
    e.preventDefault(); e.stopPropagation()
    const w = window.__hcOpen('about:blank', '_blank')
    if (w) openSigned(a.href, w)
  }, true)
  // 2) window.open(...) calls with a file address or path
  window.__hcOpen = window.open.bind(window)
  window.open = (u, ...rest) => {
    if (!isFile(String(u || '').split('#')[0])) return window.__hcOpen(u, ...rest)
    const w = window.__hcOpen('about:blank', ...rest)
    if (w) openSigned(u, w)
    return w
  }
  // 3) images, PDF frames and embeds that still carry a full file link: swap the address for a signed one as they appear
  const ATTR = { IMG: 'src', IFRAME: 'src', EMBED: 'src', OBJECT: 'data', SOURCE: 'src' }
  const isLink = u => /^https?:\/\//i.test(String(u || '')) && isFile(u)
  const fix = async nodes => {
    const els = nodes.filter(el => ATTR[el.tagName] && isLink(el.getAttribute(ATTR[el.tagName])) && !el.dataset.hcSigned)
    if (!els.length) return
    const map = await signUrls(els.map(el => el.getAttribute(ATTR[el.tagName])))
    els.forEach(el => { const orig = el.getAttribute(ATTR[el.tagName]); const s = map[orig]; if (s) { el.dataset.hcSigned = '1'; el.dataset.hcOrig = orig; el.setAttribute(ATTR[el.tagName], s) } })
  }
  const scan = root => fix(root.querySelectorAll ? [...(root.matches && root.matches('img,iframe,embed,object,source') ? [root] : []), ...root.querySelectorAll('img,iframe,embed,object,source')] : [])
  new MutationObserver(muts => {
    const nodes = []
    for (const m of muts) {
      if (m.type === 'attributes') { const el = m.target; if (el.dataset && el.dataset.hcSigned && el.getAttribute(ATTR[el.tagName]) !== el.dataset.hcOrig && isLink(el.getAttribute(ATTR[el.tagName]))) delete el.dataset.hcSigned; nodes.push(el) }
      else m.addedNodes.forEach(n => { if (n.nodeType === 1) { if (ATTR[n.tagName]) nodes.push(n); if (n.querySelectorAll) nodes.push(...n.querySelectorAll('img,iframe,embed,object,source')) } })
    }
    if (nodes.length) fix(nodes)
  }).observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['src', 'data'] })
  scan(document)
}
